import axios, { type AxiosInstance } from 'axios';
import { envConfig } from '../envConfig';
import { configureAmplify } from './lib/amplify';

let clientPromise: Promise<AxiosInstance> | null = null;

/**
 * Returns a singleton axios client whose interceptor attaches the current
 * Cognito access token (if any). Public endpoints (`AllowAnonymous`) work
 * without a token; protected endpoints will 401 until the user signs in.
 */
export async function api(): Promise<AxiosInstance> {
  if (!clientPromise) {
    clientPromise = (async () => {
      await configureAmplify();
      const client = axios.create({
        baseURL: envConfig.backendUrl,
        headers: { 'Content-Type': 'application/json' },
      });
      client.interceptors.request.use(async (config) => {
        try {
          // Lazy import keeps Amplify out of pages that don't need auth (e.g.
          // the public map page renders fine for anonymous visitors).
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const token = session.tokens?.accessToken?.toString();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // No active session - leave the request unauthenticated.
        }
        return config;
      });
      return client;
    })();
  }
  return clientPromise;
}
